import { adminEmail } from '@/shared/constants';
import type { DriveFile } from '@/shared/DriveTypes';
import { useRef } from 'react';
import { type Cache, type Middleware, mutate, type SWRHook, useSWRConfig } from 'swr/_internal';
import * as DriveAPI from '~/utils/DriveAPI';
import { concatTo } from '../fast';
import { generateQuery } from '../generateQuery';
import { log } from '../logger';
import { getId, invariant } from '../utils';

async function* getFiles(
  query: string | string[],
  email: string | undefined,
  totalFiles: DriveFile[] = [],
  driveId?: string,
  wikiId?: string,
  cache?: Cache
): AsyncGenerator<DriveFile[], DriveFile[], unknown> {
  // the first request: `'id' in parents` is always type string
  // subsequent requests can be either type string or array
  if (typeof query === 'string') {
    const children: DriveFile[] = [];
    for await (const files of DriveAPI.listStream({ query, driveId })) {
      concatTo(children, files);
      yield files;
    }

    // if any of the children have a driveId, fetch the wiki so we can check if it has augmented permissions
    let wiki: Pick<DriveFile, 'permissions'> | undefined;
    if (wikiId && children[0]?.driveId) {
      wiki =
        (cache?.get(`/drive/v3/files/${wikiId}`)?.data as DriveFile) ??
        (await DriveAPI.get({ id: wikiId, fields: 'id,driveId,hasAugmentedPermissions,permissions' }));
    }

    void mutate(`${prefix}${query}`, children, false);
    const filteredChildren = children.filter((file) => !totalFiles.some((f) => f.id === getId(file)));
    const nextQuery = generateQuery({ files: filteredChildren, email });
    if (!nextQuery) return children;
    if (import.meta.env.DEV) {
      // if any of the children are already in totalFiles throw an error in dev
      const duplicates = filteredChildren.filter((file) => totalFiles.some((f) => f.id === getId(file)));
      if (duplicates.length > 0) {
        log.error('Duplicates found in recursiveFetch:', duplicates);
      }
    }
    concatTo(totalFiles, children);
    const permission = wiki?.permissions?.find((o) => o.type === 'user' && o.emailAddress === adminEmail);
    // if the wiki admin permission has type 'file', skip passing the driveId to future requests
    const skipDriveId =
      permission && permission.type === 'user' && permission.permissionDetails?.[0].permissionType === 'file';

    return yield* getFiles(
      nextQuery,
      email,
      totalFiles,
      wikiId ? (skipDriveId ? undefined : children[0]?.driveId) : driveId
    );
  }

  const children = await Promise.all(query.map((q) => DriveAPI.list({ query: q }))).then((o) => o.flat());
  yield children;
  const filteredChildren = children.filter((file) => !totalFiles.some((f) => f.id === getId(file)));
  const nextQuery = generateQuery({ files: filteredChildren, email });
  if (!nextQuery) return children;
  if (import.meta.env.DEV) {
    // if any of the children are already in totalFiles throw an error in dev
    const duplicates = filteredChildren.filter((file) => totalFiles.some((f) => f.id === getId(file)));
    if (duplicates.length > 0) {
      log.error('Duplicates found in recursiveFetch:', duplicates);
    }
  }
  concatTo(totalFiles, children);
  return yield* getFiles(nextQuery, email, totalFiles, driveId);
}

const prefix = '/drive/v3/files?q=';
/**
 * Fetches all files recursively for a given folder
 */
export const recursiveFetch: Middleware = (useSWRNext: SWRHook) => (keyObj, stream, config) => {
  type Result = ReturnType<NonNullable<typeof stream>>;
  const { cache } = useSWRConfig();
  if (keyObj) {
    invariant(typeof keyObj === 'object' && 'key' in keyObj, 'Invalid key');
  }
  const key = (
    keyObj && typeof keyObj === 'object' && 'key' in keyObj && typeof keyObj.key === 'string' ? keyObj.key : keyObj
  ) as string;
  const keyRef = useRef(key);
  if (keyRef.current !== key) {
    keyRef.current = key;
  }

  const extendedFetcher = async ({ id, email, key }: { key: string; id: string; email: string | undefined }) => {
    const files: DriveFile[] = [];
    const query = `'${id}' in parents`;
    for await (const children of getFiles(query, email, [], undefined, id, cache)) {
      if (key !== keyRef.current) {
        return Promise.reject(new DOMException('Aborted', 'AbortError'));
      }
      if (!children) continue;
      concatTo(files, children);
      void stream?.(files);
    }
    // stream has ended
    queueMicrotask(() => stream?.([]));
    return files as Result;
  };

  return useSWRNext(key, () => extendedFetcher(keyObj as Parameters<typeof extendedFetcher>[0]), config);
};
